/**
 * Content analysis, in the shape Rank Math made familiar.
 *
 * The CMS offered a meta title and a meta description with a character counter, and nothing
 * else — no focus keyword, no idea whether the thing you just wrote was actually about the
 * thing you wanted to rank for. This file is the missing half: a set of checks that run over
 * a draft and return a score with the specific reason for every point lost.
 *
 * Deliberately dependency-free and free of React, so the same function can score a post in
 * the editor as you type and score every post in the database from a script. Nothing here
 * touches the network.
 *
 * A word on what a score means. It is a proxy, not a verdict — a 100 does not rank and an 80
 * is not a problem if the reasons are ones you chose. The value is in the itemised list: it
 * catches the mechanical omissions (keyword absent from every heading, no internal links, a
 * meta description that got truncated) that are invisible while writing and obvious later.
 */

// ── Small text helpers ───────────────────────────────────────────────────────

const stripTags = (html) => String(html || '').replace(/<[^>]+>/g, ' ');

const decodeEntities = (s) =>
  String(s || '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;|&#39;/g, "'")
    .replace(/&#8217;|&rsquo;/g, '’')
    .replace(/&mdash;|&#8212;/g, '—');

/** Plain readable text from an HTML body. */
export function toPlainText(html) {
  return decodeEntities(stripTags(html)).replace(/\s+/g, ' ').trim();
}

export function wordCount(html) {
  const t = toPlainText(html);
  if (!t) return 0;
  return t.split(/\s+/).filter((w) => /[\wÀ-ɏ]/.test(w)).length;
}

/**
 * Normalise for comparison: lowercase, fold accents, and treat curly quotes and hyphens as
 * their plain equivalents. Without this, "provider's" in the title never matches a focus
 * keyword typed as "provider's" with a straight apostrophe, and the editor is told it is
 * missing a keyword that is plainly there.
 */
function norm(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[‐-―]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** How many times the phrase occurs, matching on word boundaries. */
function countPhrase(haystack, phrase) {
  const h = norm(haystack);
  const p = norm(phrase);
  if (!h || !p) return 0;
  const re = new RegExp(`(?:^|[^\\w])${escapeRe(p)}(?:[^\\w]|$)`, 'g');
  let n = 0;
  // Overlapping matches are not a concern for phrases, but lastIndex must advance safely.
  let m;
  while ((m = re.exec(h)) !== null) {
    n += 1;
    if (re.lastIndex <= m.index) re.lastIndex = m.index + 1;
  }
  return n;
}

const hasPhrase = (haystack, phrase) => countPhrase(haystack, phrase) > 0;

/** Headings of the given levels, as plain text. */
function headings(html, levels = [2, 3, 4]) {
  const out = [];
  for (const lvl of levels) {
    const re = new RegExp(`<h${lvl}[^>]*>([\\s\\S]*?)</h${lvl}>`, 'gi');
    let m;
    while ((m = re.exec(String(html || ''))) !== null) out.push(toPlainText(m[1]));
  }
  return out;
}

function images(html) {
  return [...String(html || '').matchAll(/<img\b[^>]*>/gi)].map((m) => m[0]);
}

function links(html, siteHost) {
  const all = [...String(html || '').matchAll(/<a\b[^>]*href=["']([^"']+)["'][^>]*>/gi)];
  const internal = [];
  const external = [];
  for (const m of all) {
    const href = m[1];
    if (/^(#|mailto:|tel:|javascript:)/i.test(href)) continue;
    const isAbsolute = /^https?:\/\//i.test(href);
    const isExternal = isAbsolute && (!siteHost || !href.toLowerCase().includes(siteHost.toLowerCase()));
    (isExternal ? external : internal).push({ href, tag: m[0] });
  }
  return { internal, external };
}

// ── Word lists for the title-readability checks ──────────────────────────────
// Kept deliberately short and Australian-English. A long list produces false confidence;
// these are words that genuinely change how a headline reads in a result page.

const POWER_WORDS = [
  'complete', 'essential', 'proven', 'ultimate', 'definitive', 'practical', 'simple',
  'exactly', 'actually', 'finally', 'free', 'new', 'now', 'fast', 'easy', 'guide',
  'checklist', 'explained', 'avoid', 'stop', 'mistakes', 'without', 'never', 'best',
  'worst', 'secret', 'proof', 'real', 'honest', 'quick', 'step-by-step', 'must',
];

const SENTIMENT_WORDS = [
  'best', 'worst', 'good', 'great', 'bad', 'better', 'easy', 'hard', 'simple', 'painful',
  'safe', 'risky', 'smart', 'costly', 'wrong', 'right', 'fair', 'clear', 'honest',
  'essential', 'critical', 'avoid', 'stop', 'wins', 'fails', 'trusted', 'reliable',
];

// ── The checks ───────────────────────────────────────────────────────────────

const GROUPS = {
  basic: 'Basic SEO',
  extra: 'Additional',
  title: 'Title readability',
  content: 'Content readability',
};

/**
 * Score a draft.
 *
 * @param {object} doc
 * @param {string} doc.title            The page/post H1 or title
 * @param {string} doc.metaTitle        SEO title override (falls back to title)
 * @param {string} doc.metaDescription
 * @param {string} doc.slug
 * @param {string} doc.content          HTML body
 * @param {string} doc.excerpt
 * @param {string} doc.focusKeyword
 * @param {string[]} doc.secondaryKeywords
 * @param {string} doc.featuredImage
 * @param {string} doc.siteHost         e.g. "rostery.com.au" — used to classify links
 */
export function analyseSeo(doc = {}) {
  const {
    title = '',
    metaTitle = '',
    metaDescription = '',
    slug = '',
    content = '',
    excerpt = '',
    focusKeyword = '',
    secondaryKeywords = [],
    featuredImage = '',
    siteHost = 'rostery.com.au',
  } = doc;

  const kw = String(focusKeyword || '').trim();
  const effTitle = (metaTitle || title || '').trim();
  const plain = toPlainText(content);
  const words = wordCount(content);
  const h2h3 = headings(content, [2, 3]);
  const imgs = images(content);
  const { internal, external } = links(content, siteHost);
  const kwCount = kw ? countPhrase(plain, kw) : 0;
  const density = words > 0 && kw ? (kwCount / words) * 100 : 0;

  // First 10% of the body, which is where Rank Math looks for the keyword — and where a
  // reader decides whether the page is about what they searched for.
  const opening = plain.slice(0, Math.max(160, Math.floor(plain.length * 0.1)));

  const tests = [];
  const add = (group, id, label, ok, weight, hint, opts = {}) =>
    tests.push({ group, id, label, ok: !!ok, weight, hint, ...opts });

  // ── Basic SEO ──────────────────────────────────────────────────────────────
  if (kw) {
    add('basic', 'kw-title', 'Focus keyword appears in the SEO title',
      hasPhrase(effTitle, kw), 12,
      `Add "${kw}" to the title. It is the strongest single on-page signal and it is what a searcher sees first.`);

    add('basic', 'kw-desc', 'Focus keyword appears in the meta description',
      hasPhrase(metaDescription, kw), 8,
      `Work "${kw}" into the meta description. Google bolds the matched words in the snippet.`);

    add('basic', 'kw-slug', 'Focus keyword appears in the URL',
      hasPhrase(slug.replace(/-/g, ' '), kw), 8,
      `Include "${kw}" in the slug. Changing the slug on a published post breaks its links, so weigh this against the traffic it already has.`);

    add('basic', 'kw-opening', 'Focus keyword appears in the first paragraph',
      hasPhrase(opening, kw), 10,
      `Use "${kw}" in the opening 10% of the body. A lead that states the subject plainly is also the passage an AI answer quotes.`);

    add('basic', 'kw-content', 'Focus keyword appears in the content',
      kwCount > 0, 8,
      `The phrase "${kw}" does not appear in the body at all. Either the content is about something else, or the keyword is wrong.`,
      { value: `${kwCount} occurrence${kwCount === 1 ? '' : 's'}` });
  } else {
    add('basic', 'kw-missing', 'Set a focus keyword', false, 20,
      'Without a focus keyword nothing below can be checked against anything. Use the phrase you actually want this page found for.');
  }

  const lenOk = words >= 600;
  const lenGood = words >= 1200;
  add('basic', 'length', 'Content is long enough to be useful', lenOk, 10,
    words < 300
      ? `${words} words. Under 300 words a page is thin and rarely competitive.`
      : `${words} words. Aim for 600+, and 1200+ for anything competitive.`,
    { value: `${words} words`, partial: lenOk && !lenGood });

  // Title and description lengths — the two things that get silently truncated.
  const tLen = effTitle.length;
  add('basic', 'title-length', 'SEO title fits in a search result',
    tLen >= 25 && tLen <= 60, 6,
    tLen === 0 ? 'No title set.'
      : tLen > 60 ? `${tLen} characters — Google truncates around 60, so the end will be cut.`
      : `${tLen} characters — short. There is room to say more.`,
    { value: `${tLen}/60` });

  const dLen = metaDescription.length;
  add('basic', 'desc-length', 'Meta description fits in a search result',
    dLen >= 120 && dLen <= 158, 6,
    dLen === 0 ? 'No meta description — Google will invent one from the page.'
      : dLen > 158 ? `${dLen} characters — will be cut mid-sentence.`
      : `${dLen} characters — short. Under 120 wastes the snippet.`,
    { value: `${dLen}/158` });

  // ── Additional ─────────────────────────────────────────────────────────────
  if (kw) {
    add('extra', 'kw-heading', 'Focus keyword appears in a subheading',
      h2h3.some((h) => hasPhrase(h, kw)), 8,
      `Use "${kw}" in at least one H2 or H3. Headings are how both a skimmer and an extraction model find the relevant section.`,
      { value: `${h2h3.length} subheading${h2h3.length === 1 ? '' : 's'}` });

    add('extra', 'kw-alt', 'Focus keyword appears in an image alt attribute',
      imgs.some((tag) => {
        const m = tag.match(/alt=["']([^"']*)["']/i);
        return m && hasPhrase(m[1], kw);
      }), 4,
      imgs.length === 0
        ? 'No images in the body to carry an alt attribute.'
        : `Describe one image using "${kw}" — written for someone who cannot see it, not stuffed.`);

    const densOk = density >= 0.5 && density <= 2.5;
    add('extra', 'density', 'Keyword density is in a natural range', densOk, 6,
      density > 2.5
        ? `${density.toFixed(2)}% is repetitive. Over-repetition actively reduces AI visibility, it does not help.`
        : `${density.toFixed(2)}% is low. Use the phrase, or a close variant, where it reads naturally.`,
      { value: `${density.toFixed(2)}%` });
  }

  add('extra', 'url-length', 'URL is short', slug.length > 0 && slug.length <= 75, 3,
    slug.length === 0 ? 'No slug set.' : `${slug.length} characters. Short, readable slugs travel better.`,
    { value: `${slug.length} chars` });

  add('extra', 'internal-links', 'Links to other pages on this site', internal.length > 0, 6,
    'Add at least one internal link. It is how a new page inherits authority and how a reader gets to the next thing.',
    { value: `${internal.length} internal` });

  add('extra', 'external-links', 'Links out to a source', external.length > 0, 5,
    'Cite a source — the NDIS price guide, the award, a government page. Citations are the single largest measured boost to AI citation rates.',
    { value: `${external.length} external` });

  add('extra', 'nofollow', 'External links are followable',
    external.length === 0 || external.some((l) => !/rel=["'][^"']*nofollow/i.test(l.tag)), 2,
    'Every external link is nofollowed. At least one ordinary followed link to a source reads as a genuine citation.');

  // ── Title readability ──────────────────────────────────────────────────────
  if (kw) {
    const first = norm(effTitle).slice(0, Math.max(1, Math.floor(norm(effTitle).length * 0.5)));
    add('title', 'kw-title-start', 'Focus keyword is near the start of the title',
      hasPhrase(first, kw), 5,
      `Move "${kw}" towards the front of the title. Front-loaded titles survive truncation and read faster.`);
  }

  add('title', 'title-sentiment', 'Title carries a sentiment word',
    SENTIMENT_WORDS.some((w) => hasPhrase(effTitle, w)), 2,
    'A title with a clear positive or negative angle is clicked more often than a neutral label.');

  add('title', 'title-number', 'Title contains a number',
    /\d/.test(effTitle), 2,
    'Numbers set an expectation of specificity — a year, a count, a figure.');

  add('title', 'title-power', 'Title contains a power word',
    POWER_WORDS.some((w) => hasPhrase(effTitle, w)), 2,
    'A word like "explained", "checklist", "without" or "actually" tells the reader what shape the page is.');

  // ── Content readability ────────────────────────────────────────────────────
  add('content', 'media', 'Content contains an image or video',
    imgs.length > 0 || /<(video|iframe)\b/i.test(content) || !!featuredImage, 4,
    'Add a featured image at minimum. It is also what the link preview uses.');

  // Long paragraphs are the most common readability failure in this kind of writing.
  const paras = [...String(content || '').matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)]
    .map((m) => wordCount(m[1]))
    .filter((n) => n > 0);
  const longParas = paras.filter((n) => n > 120).length;
  add('content', 'paragraphs', 'Paragraphs are short enough to scan',
    paras.length > 0 && longParas === 0, 4,
    longParas > 0
      ? `${longParas} paragraph${longParas === 1 ? '' : 's'} over 120 words. Split them — a wall of text is skipped on a phone.`
      : 'No paragraphs found. If the body is plain text rather than HTML, this check cannot see it.',
    { value: `${paras.length} paragraphs` });

  add('content', 'subheadings', 'Content is broken up with subheadings',
    words < 300 || h2h3.length >= Math.max(2, Math.floor(words / 500)), 5,
    `${h2h3.length} subheading${h2h3.length === 1 ? '' : 's'} for ${words} words. Roughly one every 300–500 words keeps a long piece navigable.`);

  add('content', 'toc', 'Long content has a table of contents',
    words < 1800 || /<(ol|ul)[^>]*>[\s\S]*?href=["']#/i.test(content), 2,
    'Over about 1800 words, a jump list at the top earns its place — and can produce sitelinks in a result.');

  add('content', 'excerpt', 'An excerpt is set',
    String(excerpt || '').trim().length >= 40, 3,
    'Write an excerpt. It is used on the blog index and as the fallback description.');

  // ── Secondary keywords, scored lightly ─────────────────────────────────────
  const secondaries = (secondaryKeywords || []).map((s) => String(s || '').trim()).filter(Boolean);
  if (secondaries.length) {
    const found = secondaries.filter((s) => hasPhrase(plain, s));
    add('extra', 'secondary-kw', 'Secondary keywords appear in the content',
      found.length === secondaries.length, 4,
      `Missing: ${secondaries.filter((s) => !found.includes(s)).join(', ') || 'none'}.`,
      { value: `${found.length}/${secondaries.length}` });
  }

  // ── Score ──────────────────────────────────────────────────────────────────
  // Weighted pass rate. A half-credit case exists for content length: long enough to pass
  // but short of comfortable, which should not read the same as 2000 words.
  const totalWeight = tests.reduce((n, t) => n + t.weight, 0) || 1;
  const earned = tests.reduce((n, t) => {
    if (!t.ok) return n;
    return n + (t.partial ? t.weight * 0.6 : t.weight);
  }, 0);
  const score = Math.max(0, Math.min(100, Math.round((earned / totalWeight) * 100)));

  const grade = score >= 80 ? 'good' : score >= 50 ? 'ok' : 'poor';

  const grouped = Object.entries(GROUPS).map(([id, label]) => ({
    id,
    label,
    tests: tests.filter((t) => t.group === id),
  })).filter((g) => g.tests.length > 0);

  return {
    score,
    grade,
    groups: grouped,
    tests,
    passed: tests.filter((t) => t.ok).length,
    total: tests.length,
    stats: {
      words,
      keywordCount: kwCount,
      density: Number(density.toFixed(2)),
      headings: h2h3.length,
      images: imgs.length,
      internalLinks: internal.length,
      externalLinks: external.length,
      titleLength: effTitle.length,
      descriptionLength: metaDescription.length,
      readingMinutes: Math.max(1, Math.round(words / 220)),
    },
  };
}

export const SCHEMA_TYPES = {
  blog:    ['BlogPosting', 'Article', 'NewsArticle', 'HowTo', 'FAQPage', 'None'],
  feature: ['Service', 'Product', 'SoftwareApplication', 'WebPage', 'FAQPage', 'None'],
  page:    ['WebPage', 'AboutPage', 'ContactPage', 'CollectionPage', 'FAQPage', 'None'],
};

export default analyseSeo;
